// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/channel/components/ChannelChatLayout.module.css';
import {setHolidayFlybyHost} from '@app/features/holiday/utils/holidayFlybyHost';
import {
	setHolidayScareComposerInset,
	setHolidayScareHost,
	subscribeHolidayScareHost,
} from '@app/features/holiday/utils/holidayScareHost';
import {
	setHolidaySidePeekComposerInset,
	subscribeHolidaySidePeekHost,
} from '@app/features/holiday/utils/holidaySidePeekHost';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useRef} from 'react';

const MESSAGES_DESCRIPTOR = msg({
	message: 'Messages',
	comment: 'Short label in the channel chat layout. Keep it concise.',
});
const MESSAGE_COMPOSER_DESCRIPTOR = msg({
	message: 'Message composer',
	comment: 'Short label in the channel chat layout. Keep it concise.',
});

interface ChannelChatLayoutProps {
	messages: React.ReactNode;
	textarea: React.ReactNode;
}

export const ChannelChatLayout = observer(({messages, textarea}: ChannelChatLayoutProps) => {
	const {i18n} = useLingui();
	const bindHolidayFlybyHost = useCallback((node: HTMLDivElement | null) => {
		if (node == null) return;
		setHolidayFlybyHost(node);
		return () => {
			setHolidayFlybyHost(null, node);
		};
	}, []);
	const bindHolidayScareHost = useCallback((node: HTMLDivElement | null) => {
		if (node == null) return;
		setHolidayScareHost(node);
		return () => {
			setHolidayScareHost(null, node);
		};
	}, []);
	const textareaAreaRef = useRef<HTMLElement | null>(null);
	useEffect(() => {
		const node = textareaAreaRef.current;
		if (node == null) return;
		const sync = (): void => {
			const height = node.getBoundingClientRect().height;
			setHolidaySidePeekComposerInset(height);
			setHolidayScareComposerInset(height);
		};
		const observer = new ResizeObserver(sync);
		observer.observe(node);
		const unsubscribeSidePeek = subscribeHolidaySidePeekHost(sync);
		const unsubscribeScare = subscribeHolidayScareHost(sync);
		sync();
		return () => {
			observer.disconnect();
			unsubscribeSidePeek();
			unsubscribeScare();
			setHolidaySidePeekComposerInset(null);
			setHolidayScareComposerInset(null);
		};
	}, []);
	return (
		<div className={styles.container} data-flx="channel.channel-chat-layout.container">
			<div
				ref={bindHolidayScareHost}
				className={styles.scareHost}
				aria-hidden="true"
				data-flx="holiday.holiday-layer.scare-host"
			/>
			<div
				ref={bindHolidayFlybyHost}
				className={styles.flybyHost}
				aria-hidden="true"
				data-flx="holiday.holiday-layer.flybys-host"
			/>
			<section
				className={styles.messagesArea}
				aria-label={i18n._(MESSAGES_DESCRIPTOR)}
				data-flx="channel.channel-chat-layout.messages-area"
			>
				{messages}
			</section>
			<section
				ref={textareaAreaRef}
				className={styles.textareaArea}
				aria-label={i18n._(MESSAGE_COMPOSER_DESCRIPTOR)}
				data-flx="channel.channel-chat-layout.textarea-area"
			>
				{textarea}
			</section>
		</div>
	);
});
